import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, ChefHat, Calculator } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface RawMaterial {
  id: string;
  name: string;
  unit_type: string;
  current_stock: number;
  cost_per_unit: number;
}

interface RecipeIngredient {
  id?: string;
  material_id: string;
  material_name: string;
  quantity: number;
  unit: string;
  cost_per_unit: number;
}

interface Recipe {
  id?: string;
  menu_item_id: string;
  name: string;
  description: string;
  portion_size: number;
  ingredients: RecipeIngredient[];
}

interface RecipeManagerProps {
  menuItem: { id: string; name: string };
  onRecipeUpdated?: () => void;
  selectedBranchId?: string;
}

export const RecipeManager: React.FC<RecipeManagerProps> = ({ menuItem, onRecipeUpdated, selectedBranchId }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [materials, setMaterials] = useState<RawMaterial[]>([]);
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [loading, setLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    portion_size: 1
  });
  const [ingredients, setIngredients] = useState<RecipeIngredient[]>([]);
  const [newIngredient, setNewIngredient] = useState({
    material_id: '',
    quantity: 0,
    unit: ''
  });

  useEffect(() => {
    if (user) {
      loadMaterials();
      loadRecipe();
    }
  }, [user, menuItem.id]);

  const loadMaterials = async () => {
    try {
      const { data: materialsData, error: materialsError } = await (supabase as any)
        .from('raw_materials')
        .select('id, name, unit_type, current_stock, cost_per_unit')
        .eq('user_id', user.id)
        .eq('branch_id', selectedBranchId)
        .order('name');

      if (materialsError) {
        console.error('Error loading materials:', materialsError);
      } else {
        setMaterials(materialsData || []);
      }
    } catch (error) {
      console.error('Error loading materials:', error);
    }
  };

  const loadRecipe = async () => {
    try {
      const { data: recipeData, error: recipeError } = await (supabase as any)
        .from('recipes')
        .select(`
          id,
          menu_item_id,
          name,
          description,
          portion_size,
          recipe_ingredients (
            id,
            material_id,
            quantity,
            unit
          )
        `)
        .eq('menu_item_id', menuItem.id)
        .eq('user_id', user.id)
        .single();

      if (recipeError && recipeError.code !== 'PGRST116') { // PGRST116 is "not found"
        console.error('Error loading recipe:', recipeError);
      } else if (recipeData) {
        // Load material names for ingredients
        const materialIds = recipeData.recipe_ingredients?.map(ing => ing.material_id) || [];
        const materialNames: Record<string, { name: string; cost_per_unit: number }> = {};
        
        if (materialIds.length > 0) {
          const { data: materialsData } = await (supabase as any)
            .from('raw_materials')
            .select('id, name, cost_per_unit')
            .in('id', materialIds);
          
          materialsData?.forEach(material => {
            materialNames[material.id] = {
              name: material.name,
              cost_per_unit: material.cost_per_unit
            };
          });
        }

        // Transform recipe data to match expected format
        const transformedRecipe = {
          ...recipeData,
          ingredients: (recipeData.recipe_ingredients || []).map(ingredient => ({
            id: ingredient.id,
            material_id: ingredient.material_id,
            material_name: materialNames[ingredient.material_id]?.name || 'Unknown Material',
            quantity: ingredient.quantity,
            unit: ingredient.unit,
            cost_per_unit: materialNames[ingredient.material_id]?.cost_per_unit || 0
          }))
        };

        setRecipe(transformedRecipe);
        setFormData({
          name: transformedRecipe.name,
          description: transformedRecipe.description || '',
          portion_size: transformedRecipe.portion_size
        });
        setIngredients(transformedRecipe.ingredients || []);
      }
    } catch (error) {
      console.error('Error loading recipe:', error);
    }
  };

  const handleSaveRecipe = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const recipeData = {
        menu_item_id: menuItem.id,
        name: formData.name,
        description: formData.description,
        portion_size: formData.portion_size,
        user_id: user.id,
        branch_id: selectedBranchId // Add branch_id to recipe data
      };

      let result;
      if (recipe) {
        // Update existing recipe
        const { data, error } = await (supabase as any)
          .from('recipes')
          .update(recipeData)
          .eq('id', recipe.id)
          .eq('user_id', user.id)
          .select()
          .single();
        
        if (error) throw error;
        
        // Delete existing recipe ingredients
        await (supabase as any)
          .from('recipe_ingredients')
          .delete()
          .eq('recipe_id', recipe.id);
        
        result = { success: true, data };
      } else {
        // Create new recipe
        const { data, error } = await (supabase as any)
          .from('recipes')
          .insert(recipeData)
          .select()
          .single();
        
        if (error) throw error;
        result = { success: true, data };
      }

      // Save recipe ingredients
      if (result.success && ingredients.length > 0) {
        const recipeIngredientsData = ingredients.map(ingredient => ({
          recipe_id: result.data.id,
          material_id: ingredient.material_id,
          quantity: ingredient.quantity,
          unit: ingredient.unit,
          user_id: user.id
        }));

        const { error: ingredientsError } = await (supabase as any)
          .from('recipe_ingredients')
          .insert(recipeIngredientsData);

        if (ingredientsError) {
          console.error('Error saving recipe ingredients:', ingredientsError);
          throw new Error('Failed to save recipe ingredients');
        }
      }

      if (result.success) {
        toast({ title: 'Recipe saved successfully' });
        setIsDialogOpen(false);
        loadRecipe();
        onRecipeUpdated?.();
      } else {
        throw new Error('Failed to save recipe');
      }
    } catch (error) {
      console.error('Error saving recipe:', error);
      toast({
        title: 'Error saving recipe',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const addIngredient = () => {
    if (!newIngredient.material_id || newIngredient.quantity <= 0) {
      toast({
        title: 'Invalid ingredient',
        description: 'Please select a material and enter a valid quantity',
        variant: 'destructive'
      });
      return;
    }

    const material = materials.find(m => m.id === newIngredient.material_id);
    if (!material) return;

    const ingredient: RecipeIngredient = {
      material_id: newIngredient.material_id,
      material_name: material.name,
      quantity: newIngredient.quantity,
      unit: material.unit_type, // Always use material's unit type
      cost_per_unit: material.cost_per_unit
    };

    setIngredients([...ingredients, ingredient]);
    setNewIngredient({ material_id: '', quantity: 0, unit: '' });
  };

  const removeIngredient = (index: number) => {
    setIngredients(ingredients.filter((_, i) => i !== index));
  };

  const calculateTotalCost = () => {
    return ingredients.reduce((total, ing) => {
      return total + (ing.quantity * ing.cost_per_unit);
    }, 0);
  };

  const openRecipeDialog = () => {
    if (recipe) {
      // For editing, use existing recipe data
      setFormData({
        name: recipe.name,
        description: recipe.description,
        portion_size: recipe.portion_size
      });
      setIngredients(recipe.ingredients);
    } else {
      // For creating new recipe, auto-generate sensible defaults
      setFormData({ 
        name: `${menuItem.name} Recipe`, // Auto-generate recipe name
        description: `Recipe for ${menuItem.name}`, // Auto-generate description
        portion_size: 1 
      });
      setIngredients([]);
    }
    setIsDialogOpen(true);
  };

  // Show a warning if any ingredient references a material not present in the current branch
  const missingMaterials = ingredients.filter(ing => !materials.find(m => m.id === ing.material_id));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ChefHat className="h-5 w-5 text-orange-600" />
          <h3 className="text-lg font-semibold">Recipe for {menuItem.name}</h3>
        </div>
        <Button onClick={openRecipeDialog} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          {recipe ? 'Edit Ingredients' : 'Add Ingredients'}
        </Button>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {recipe ? `Edit Recipe for ${menuItem.name}` : `Create Recipe for ${menuItem.name}`}
            </DialogTitle>
            <DialogDescription>
              Add ingredients and quantities needed for this menu item.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {missingMaterials.length > 0 && (
              <div className="bg-yellow-100 text-yellow-800 rounded p-2 text-sm mb-2">
                Warning: Some ingredients reference materials that are not present in this branch. Their cost will be shown as 0 in analytics.
              </div>
            )}
            {/* Auto-generated fields - hidden but still functional */}
            <div className="hidden">
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
              <Input
                type="number"
                value={formData.portion_size}
                onChange={(e) => setFormData({ ...formData, portion_size: parseFloat(e.target.value) || 1 })}
              />
            </div>

            {/* Ingredients Section - This is what matters */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium">Ingredients</h4>
                <div className="text-sm text-gray-600">
                  Total Cost: EGP {calculateTotalCost().toFixed(2)}
                </div>
              </div>
              
              <div className="space-y-3">
                {ingredients.map((ingredient, index) => (
                  <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium">{ingredient.material_name}</div>
                      <div className="text-sm text-gray-600">
                        {ingredient.quantity} {ingredient.unit} × ${ingredient.cost_per_unit} = ${(ingredient.quantity * ingredient.cost_per_unit).toFixed(2)}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeIngredient(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Add Ingredient Form */}
              <div className="space-y-3 mt-4 p-4 border rounded-lg bg-gray-50">
                <h5 className="font-medium text-sm">Add Ingredient</h5>
                <div className="grid grid-cols-3 gap-2">
                  <Select
                    value={newIngredient.material_id}
                    onValueChange={value => setNewIngredient({ ...newIngredient, material_id: value })}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Select material" />
                    </SelectTrigger>
                    <SelectContent>
                      {materials.map(material => (
                        <SelectItem key={material.id} value={material.id}>
                          {material.name} (EGP {material.cost_per_unit})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newIngredient.quantity}
                    onChange={e => setNewIngredient({ ...newIngredient, quantity: parseFloat(e.target.value) || 0 })}
                    placeholder="Quantity"
                    className="w-24"
                  />
                  <Button onClick={addIngredient} size="sm" variant="outline">
                    Add
                  </Button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveRecipe} disabled={loading || ingredients.length === 0}>
                {loading ? 'Saving...' : (recipe ? 'Update Recipe' : 'Create Recipe')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {recipe && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ChefHat className="h-5 w-5" />
              {recipe.name}
            </CardTitle>
            {recipe.description && (
              <p className="text-sm text-gray-600">{recipe.description}</p>
            )}
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Portion Size:</span>
                <Badge variant="secondary">{recipe.portion_size}</Badge>
              </div>

              <div className="space-y-2">
                <h4 className="font-medium">Ingredients:</h4>
                {recipe.ingredients.map((ingredient) => (
                  <div key={ingredient.material_id} className="flex items-center justify-between text-sm">
                    <span>{ingredient.material_name}</span>
                    <span className="text-gray-600">
                      {ingredient.quantity} {ingredient.unit}
                    </span>
                  </div>
                ))}
              </div>

              <div className="border-t pt-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Total Cost:</span>
                  <span className="text-lg font-semibold text-green-600">
                    ${calculateTotalCost().toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}; 